/* mt-blog-hub.js — runtime for the /pages/blogs hub sections
   (mt-blog-hero, mt-blog-rail, mt-blog-shop).

   Three behaviours, all driven off markup rather than section ids so one
   file serves every instance on the page:

     .mt-reveal          fade-and-rise as it scrolls into view
     [data-typewriter]   types the heading out; "|" is a line break and a
                         {braced} run becomes .mt-tw-em
     .mt-bh-rail-sec     arrow paging, scroll snapping, progress + counter

   Everything is idempotent — each root and each element is flagged once it
   has been wired — so the theme editor's shopify:section:load can re-run
   init() over a re-rendered section without doubling anything up. */

(function () {
  'use strict';

  /* Every hub section asks for this file, so the page carries one tag per
     section and the browser executes each of them. The sections are deferred,
     so by the time the first copy runs the whole document is parsed and its
     init() already sees every root — the later copies have nothing to do.
     (The per-element flags below would make them harmless anyway; this just
     keeps them from re-walking the DOM and stacking observers.) */
  if (window.__mtBlogHubReady) return;
  window.__mtBlogHubReady = true;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Reveal on scroll ───────────────────────────────────── */
  function initReveal(root) {
    var items = root.querySelectorAll('.mt-reveal:not(.is-in)');
    if (!items.length) return;

    if (!('IntersectionObserver' in window) || reduceMotion) {
      Array.prototype.forEach.call(items, function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -36px 0px' }
    );

    Array.prototype.forEach.call(items, function (el) {
      obs.observe(el);
    });
  }

  /* ── Typewriter heading ─────────────────────────────────── */

  /* Builds the "{…}" emphasis markup and "|" line breaks into a host. */
  function renderRuns(host, text) {
    var target = host;
    for (var i = 0; i < text.length; i++) {
      var ch = text[i];
      if (ch === '|') {
        host.appendChild(document.createElement('br'));
      } else if (ch === '{') {
        var em = document.createElement('span');
        em.className = 'mt-tw-em';
        host.appendChild(em);
        target = em;
      } else if (ch === '}') {
        target = host;
      } else {
        target.appendChild(document.createTextNode(ch));
      }
    }
  }

  function initTypewriter(root) {
    Array.prototype.forEach.call(
      root.querySelectorAll('[data-typewriter]:not([data-tw-on])'),
      function (el) {
        el.setAttribute('data-tw-on', '1');

        var text = el.getAttribute('data-text') || el.textContent;
        var speed = parseInt(el.getAttribute('data-speed') || '30', 10);
        var initial = parseInt(el.getAttribute('data-initial-delay') || '250', 10);

        /* Reduced motion: show the finished markup, never type. */
        if (reduceMotion) {
          el.innerHTML = '';
          var flat = document.createElement('span');
          flat.className = 'mt-tw-content';
          renderRuns(flat, text);
          el.appendChild(flat);
          return;
        }

        el.innerHTML = '';
        var span = document.createElement('span');
        span.className = 'mt-tw-content';
        var cursor = document.createElement('span');
        cursor.className = 'mt-tw-cursor';
        cursor.textContent = '|';
        el.appendChild(span);
        el.appendChild(cursor);

        var i = 0;
        var target = span;

        function tick() {
          if (i < text.length) {
            var ch = text[i++];
            if (ch === '|') {
              span.appendChild(document.createElement('br'));
            } else if (ch === '{') {
              var em = document.createElement('span');
              em.className = 'mt-tw-em';
              span.appendChild(em);
              target = em;
            } else if (ch === '}') {
              target = span;
            } else {
              target.appendChild(document.createTextNode(ch));
            }
            setTimeout(tick, speed);
          } else {
            /* Short beat so the caret reads as "finished" rather than cutting
               out mid-stroke, then it goes. 900ms — the older sections' value —
               is long enough to look like it is still waiting for input. */
            setTimeout(function () {
              cursor.classList.add('is-done');
              /* The fade is 0.4s (see the .is-done rule in this section's
                 stylesheet); once it is done the caret is taken out of the DOM, not just
                 left at opacity 0. Invisible is not gone: it is an inline-block with a
                 left margin, so it went on holding a gap at the end of the finished
                 heading. A timer rather than an animationend listener, so it is also
                 removed if the stylesheet never applied. */
              setTimeout(function () { if (cursor.parentNode) { cursor.parentNode.removeChild(cursor); } }, 450);
            }, 250);
          }
        }

        setTimeout(tick, initial);
      }
    );
  }

  /* ── Article rail ───────────────────────────────────────── */
  function initRail(sec) {
    if (sec.getAttribute('data-bh-rail-on')) return;

    var scroller = sec.querySelector('.mt-bh-rail');
    if (!scroller) return;

    var cards = scroller.querySelectorAll('.mt-bh-card');
    if (!cards.length) return;

    sec.setAttribute('data-bh-rail-on', '1');

    var prev = sec.querySelector('[data-bh-prev]');
    var next = sec.querySelector('[data-bh-next]');
    var bar = sec.querySelector('.mt-bh-progress span');
    var count = sec.querySelector('.mt-bh-count');
    var total = cards.length;

    function pad(n) {
      return String(n).length < 2 ? '0' + n : String(n);
    }

    /* One "page" is one card-step, as the design has it: with 4 cards three
       across there are 2 stops, not 2 screens-worth. Measured from the DOM
       rather than a setting so the CSS breakpoints stay the one source of
       truth for how many cards are actually in view. */
    function measure() {
      var first = scroller.firstElementChild;
      var cardW = first ? first.getBoundingClientRect().width : 0;
      var per = cardW ? Math.max(1, Math.round(scroller.clientWidth / cardW)) : 1;
      var pages = Math.max(1, total - per + 1);
      var step = scroller.scrollWidth / total;
      var page = step ? Math.min(pages - 1, Math.round(scroller.scrollLeft / step)) : 0;

      sec.classList.toggle('is-static', pages <= 1);

      if (bar) {
        bar.style.width = 100 / pages + '%';
        bar.style.left = (page * 100) / pages + '%';
      }
      if (count) {
        count.textContent = pad(page + 1) + ' / ' + pad(pages);
      }
      if (prev) prev.disabled = page <= 0;
      if (next) next.disabled = page >= pages - 1;
    }

    function nudge(dir) {
      scroller.scrollBy({
        left: dir * (scroller.scrollWidth / total),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });

    /* rAF-coalesced: a smooth scroll fires this dozens of times a frame. */
    var queued = false;
    scroller.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          measure();
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', measure);

    /* The cards carry lazy images, so the first measure can land before the
       tracks have their final width. ResizeObserver catches that; the
       timeout is the fallback where it is missing. */
    if ('ResizeObserver' in window) {
      new ResizeObserver(measure).observe(scroller);
    } else {
      setTimeout(measure, 300);
    }

    measure();
  }

  /* ── Init ───────────────────────────────────────────────── */
  function init(scope) {
    var roots = (scope || document).querySelectorAll('.mt-bh');

    Array.prototype.forEach.call(roots, function (root) {
      /* Arms the reveal transitions — without this class the CSS leaves the
         content visible, which is the no-JS state. */
      root.classList.add('mt-bh-js');
      initReveal(root);
      initTypewriter(root);
    });

    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('.mt-bh-rail-sec'),
      initRail
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor: a re-rendered section arrives detached from the pass above. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
